import { useCallback, useEffect, useRef, useState } from "react";
import { FaExclamationTriangle } from "../utils/icons";
import { useLocation } from "react-router-dom";
import { decodeJwtPayload } from "../utils/jwt";

// TEMP: flip to true to re-enable student content protection (devtools block, right-click block, etc.)
const PROTECTION_ENABLED = false;

export default function ContentProtection() {
  const location = useLocation();

  const token = localStorage.getItem("token");
  const payload = token ? decodeJwtPayload(token) : null;
  const role = (payload?.role || "").toLowerCase();
  const isStudent = PROTECTION_ENABLED && role === "student";
  // Right-click blocking runs independently of PROTECTION_ENABLED (which also gates
  // devtools detection and printscreen/clipboard handling — not asked for here).
  const isStudentRole = role === "student";

  // Overlay is always in DOM â€" controlled via direct ref for zero-latency show/hide
  // (React state batching is too slow to beat OS screenshot capture timing)
  const overlayRef = useRef<HTMLDivElement>(null);
  const devToolsOpenRef = useRef(false);
  const printScreenRef = useRef(false);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // React state only drives the DevTools message text inside the overlay
  const [devToolsMsg, setDevToolsMsg] = useState(false);

  useEffect(() => {
    if (!isStudentRole) return;
    const blockCtx = (e: MouseEvent) => e.preventDefault();
    document.addEventListener("contextmenu", blockCtx);
    return () => document.removeEventListener("contextmenu", blockCtx);
  }, [isStudentRole]);

  const showOverlay = useCallback(() => {
    if (overlayRef.current) overlayRef.current.style.visibility = "visible";
  }, []);

  const tryHideOverlay = useCallback(() => {
    if (!devToolsOpenRef.current && !printScreenRef.current) {
      if (overlayRef.current) overlayRef.current.style.visibility = "hidden";
    }
  }, []);

  useEffect(() => {
    if (!isStudent) {
      devToolsOpenRef.current = false;
      printScreenRef.current = false;
      setDevToolsMsg(false);
      if (overlayRef.current) overlayRef.current.style.visibility = "hidden";
      return;
    }

    // â"€â"€ Keyboard handler â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
    const handleKeyDown = (e: KeyboardEvent) => {
      const k = e.key.toUpperCase();

      // Block DevTools shortcuts
      if (e.key === "F12") { e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["I", "J", "C", "K"].includes(k)) {
        e.preventDefault(); return;
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && ["U", "S", "P"].includes(k)) {
        e.preventDefault(); return;
      }

      // PrintScreen: show black BEFORE OS captures (keydown fires before keyup capture)
      if (e.key === "PrintScreen") {
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        printScreenRef.current = true;
        showOverlay();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "PrintScreen") {
        // Overwrite clipboard so any captured image is replaced
        navigator.clipboard.write([
          new ClipboardItem({ "image/png": new Blob([], { type: "image/png" }) }),
        ]).catch(() => {
          navigator.clipboard.writeText("").catch(() => {});
        });
        // Keep black for 300 ms then restore
        if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
        clearTimerRef.current = setTimeout(() => {
          printScreenRef.current = false;
          tryHideOverlay();
        }, 300);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("keyup", handleKeyUp, true);

    // â"€â"€ Disable media drag â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
    const blockDrag = (e: DragEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "IMG" || tag === "VIDEO") e.preventDefault();
    };
    document.addEventListener("dragstart", blockDrag);

    // â"€â"€ DevTools detection via window size â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
    const checkDevTools = () => {
      const wDiff = window.outerWidth  - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      const isOpen = wDiff > 160 || hDiff > 160;
      if (isOpen !== devToolsOpenRef.current) {
        devToolsOpenRef.current = isOpen;
        setDevToolsMsg(isOpen);
        if (isOpen) showOverlay();
        else tryHideOverlay();
      }
    };
    checkDevTools();
    const dtInterval = setInterval(checkDevTools, 1000);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("keyup", handleKeyUp, true);
      document.removeEventListener("dragstart", blockDrag);
      clearInterval(dtInterval);
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    };
  }, [isStudent, location.pathname, showOverlay, tryHideOverlay]);

  if (!isStudent) return null;

  return (
    <>
      {/* Hide all content when printing */}
      <style>{`
        @media print {
          body > * { visibility: hidden !important; }
          body::after {
            content: "This content is protected and cannot be printed.";
            visibility: visible !important;
            position: fixed; inset: 0;
            display: flex; align-items: center; justify-content: center;
            font-size: 1.2rem; color: #333;
          }
        }
      `}</style>

      {/* Black protection overlay â€" always in DOM, shown via direct ref for max speed */}
      <div
        ref={overlayRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "#000",
          zIndex: 99999,
          visibility: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          textAlign: "center",
          padding: "24px",
          userSelect: "none",
        }}
      >
        {devToolsMsg && (
          <>
            <FaExclamationTriangle style={{ fontSize: "52px", marginBottom: "20px", color: '#f59e0b' }} />
            <h2 style={{ fontSize: "1.4rem", fontWeight: 700, marginBottom: "12px" }}>
              Developer Tools Detected
            </h2>
            <p style={{ color: "#94a3b8", maxWidth: "380px", lineHeight: 1.6, fontSize: "0.92rem" }}>
              Content is protected and cannot be accessed while developer tools are open.
              Please close them to continue learning.
            </p>
          </>
        )}
      </div>
    </>
  );
}
